import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from '@xing-com/button';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { useSessionStorage } from '@xing-com/crate-hooks-use-web-storage';
import {
  AI_SEARCH_FEEDBACK_SURVEY_URL,
  AI_SEARCH_FEEDBACK_SURVEY_URL_SMART_INVENTORY,
} from '@xing-com/crate-jobs-constants';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { useExperiment, useViewerData } from '@xing-com/hub';
import { mediaConfined, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { scale150, spaceL } from '@xing-com/tokens';
import { BodyCopy, Headline } from '@xing-com/typography';

import { useHideAside } from '../../../hooks';

import { AiFeedbackCard } from './ai-feedback-card';
import type { FeedbackVariant } from './color-feedback';
import { FeedbackContent } from './feedback-content';
import { useForceShow } from './use-force-show';

const SESSION_STORAGE_KEY = 'ai-feedback-shown';

const SURVEY_HEADLINE: Record<FeedbackVariant, string> = {
  like: 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_LIKE',
  neutral: 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_NEUTRAL',
  dislike: 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_DISLIKE',
};

const SURVEY_COPY: Record<FeedbackVariant, string> = {
  like: 'JOBS_SEARCH_AI_FEEDBACK_COPY_LIKE',
  neutral: 'JOBS_SEARCH_AI_FEEDBACK_COPY_NEUTRAL',
  dislike: 'JOBS_SEARCH_AI_FEEDBACK_COPY_DISLIKE',
};

const SURVEY_REACTION_PARAM: Record<FeedbackVariant, string> = {
  like: 'L',
  neutral: 'N',
  dislike: 'D',
};

type Props = {
  searchId?: string;
  trackingToken?: string;
  isStickyHeader?: boolean;
};

export const AIFeedback: React.FC<Props> = ({
  searchId,
  trackingToken,
  isStickyHeader,
}) => {
  const [surveyType, setSurveyType] = useState<FeedbackVariant | null>(null);
  const { data } = useViewerData();
  const hashedId = data?.hashedUserId || '0';
  const trackNwt = useJobsNwtTracking();
  const isTrackedRef = useRef(false);

  const isSmartInventory = useExperiment('ABACUS-497') === 'B';

  const hideAside = useHideAside();
  const isBigScreen = useMatchMedia(mediaConfined);
  const [showInSession, setShowInSession] = useSessionStorage(
    SESSION_STORAGE_KEY,
    true
  );
  const forceShow = useForceShow(Boolean(isStickyHeader || isBigScreen));
  const show = Boolean(
    searchId &&
      trackingToken &&
      showInSession &&
      !hideAside &&
      (isStickyHeader || isBigScreen || forceShow)
  );

  const handleOnClick = (feedback: FeedbackVariant): void => {
    setSurveyType(feedback);
  };

  const handleOnClose = (): void => {
    setShowInSession(false);
  };

  const handleOnEntered = (): void => {
    if (isTrackedRef.current) {
      return;
    }

    trackNwt({
      eventSchema: 'aicomponent',
      page: 'jobs_search/serp',
      event: 'entered_viewport',
      componentName: 'ai_search',
      componentFeedbackLevel: '2',
      trackingToken,
    });

    isTrackedRef.current = true;
  };

  const headline = surveyType
    ? SURVEY_HEADLINE[surveyType]
    : 'JOBS_SEARCH_AI_FEEDBACK_HEADLINE_V2';
  const copy = surveyType
    ? SURVEY_COPY[surveyType]
    : 'JOBS_SEARCH_AI_FEEDBACK_COPY';

  const url = isSmartInventory
    ? AI_SEARCH_FEEDBACK_SURVEY_URL_SMART_INVENTORY
    : AI_SEARCH_FEEDBACK_SURVEY_URL;
  const urlParams = new URLSearchParams({
    r: `${hashedId}_${trackingToken}`,
    ...(surveyType && { h: SURVEY_REACTION_PARAM[surveyType] }),
  });
  const surveyUrl = `${url}?${urlParams.toString()}`;

  return (
    <AiFeedbackCard
      show={show}
      onClose={handleOnClose}
      onEntered={handleOnEntered}
    >
      <Title size="large" noMargin $hideOnMobile={!surveyType}>
        <FormattedMessage id={headline} />
      </Title>
      <Text noMargin>
        <FormattedMessage id={copy} />
      </Text>
      {!surveyType ? (
        <FeedbackContent
          onClick={handleOnClick}
          trackingToken={trackingToken}
        />
      ) : (
        <Button
          variant="secondary"
          href={surveyUrl}
          // @ts-expect-error FIXME: SC6
          target="_blank"
          rel="noopener"
          onClick={handleOnClose}
        >
          <FormattedMessage id="JOBS_SEARCH_AI_FEEDBACK_CTA" />
        </Button>
      )}
    </AiFeedbackCard>
  );
};

const Title = styled(Headline)<{ $hideOnMobile: boolean }>`
  // close button width - card padding, to avoid text going under the close button
  padding-inline: calc(${scale150} - ${spaceL});

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    css`
      @media ${mediaSmallOrTiny} {
        display: none;
      }
    `}
`;

const Text = styled(BodyCopy)`
  // close button width - card padding, to avoid text going under the close button
  padding-inline: calc(${scale150} - ${spaceL});
`;
